<template>
  <div class="project-hero project-hero--simple" data-qid="project-hero">
    <div class="project-hero__top">
      <div class="project-hero__main">
        <router-link v-if="project.project && project.project.id"
                     :to="{ name: 'projects.view', params: { slug: project.project.slug }}"
                     class="project-hero__crumb"
                     data-qid="project-hero-parent"
        >
          <font-awesome-icon icon="chevron-left" class="mr-1"/>{{ project.project.title }}
        </router-link>
        <router-link v-else
                     to="/projects?view=list"
                     class="project-hero__crumb"
                     data-qid="project-hero-list"
        >
          <font-awesome-icon icon="chevron-left" class="mr-1"/>{{ $t('projects.management.title') }}
        </router-link>
        <div class="project-hero__eyebrow">
          <span v-for="(part, index) in eyebrow" :key="'eyebrow-' + index">{{ part }}</span>
        </div>
        <h1 class="project-hero__title" data-qid="p-view-page-title">{{ project.title }}</h1>

        <div class="project-hero__pills">
          <span class="hero-pill" :class="statusClass" data-qid="project-hero-status">
            <font-awesome-icon :icon="statusIcon"/>{{ $t('system.' + $options.filters.slugify(project.status || 'pending')) }}
          </span>
          <span v-for="tag in tags"
                :key="'hero-tag-' + tag.name_translated"
                class="hero-pill hero-pill--tag"
                data-qid="project-hero-tag"
          >
            <font-awesome-icon icon="tag"/>{{ tag.name_translated }}
          </span>
          <span class="project-hero__team" v-if="team.length">
            <Avatar v-for="user in team.slice(0, 4)"
                    :key="'hero-user-' + user.id"
                    :object="user"
                    size="xs"
                    v-b-tooltip.hover
                    :title="user.name"
            ></Avatar>
            <span v-if="team.length > 4" class="project-hero__more">+{{ team.length - 4 }}</span>
          </span>
        </div>

        <div class="project-hero__facts">
          <div v-if="project.start_date">
            {{ $t('projects.schedule.start_date_label') }}
            <b>{{ project.start_date | formatDate('DD MMM', true) }} · {{ relative(project.start_date) }}</b>
          </div>
          <div v-if="project.end_date">
            {{ $t('projects.engagement.hero.project_due') }}
            <b :class="{'project-hero__over': isOverdue}">{{ project.end_date | formatDate('DD MMM', true) }} · {{ relative(project.end_date) }}</b>
          </div>
        </div>

        <div class="project-hero__actions">
          <slot name="actions"></slot>
        </div>
      </div>

      <!-- Task progress -->
      <div class="project-hero__progress" data-qid="project-hero-progress">
        <div class="project-hero__ring">
          <svg width="96" height="96" viewBox="0 0 96 96">
            <circle class="project-hero__ring-track"
                    cx="48"
                    cy="48"
                    r="40"
            />
            <circle class="project-hero__ring-value"
                    cx="48"
                    cy="48"
                    r="40"
                    :stroke-dasharray="ringDash"
                    transform="rotate(-90 48 48)"
            />
          </svg>
          <b>{{ progress }}%</b>
        </div>
        <div class="project-hero__progress-text">
          <div class="project-hero__progress-title">{{ $t('projects.hero.task_progress') }}</div>
          <div class="project-hero__legend">
            <span v-if="taskCount">{{ $tc('projects.hero.task_count', taskCount, { count: taskCount }) }}</span>
            <span v-else>{{ $t('projects.hero.no_tasks') }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Avatar from '@/components/Avatar.vue';

// Header of a one-time project that isn't an engagement: type, status, team, dates and task progress (US 9478)
export default {
  name: 'ProjectHero',
  props: {
    project: {
      type: Object,
      required: true
    }
  },
  components: {
    Avatar
  },
  computed: {
    team() {
      return this.project.users || [];
    },
    tags() {
      return this.project.tags_translated || [];
    },
    eyebrow() {
      let parts = [];
      if (this.project.type) {
        parts.push(this.$t('projects.management.types.' + this.project.type));
      }
      if (this.project.assessment && this.project.assessment.name && !parts.includes(this.project.assessment.name)) {
        parts.push(this.project.assessment.name);
      }
      if (this.project.scope) {
        parts.push(this.$t('projects.launcher.scopes.' + this.project.scope));
      }
      return parts;
    },
    statusClass() {
      return {
        'hero-pill--warn': this.project.status === 'in progress' || this.project.status === 'pending',
        'hero-pill--ok': this.project.status === 'completed'
      };
    },
    statusIcon() {
      const icons = { completed: 'check-circle', closed: 'archive', 'in progress': 'clock', pending: 'hourglass-half' };
      return icons[this.project.status] || 'clock';
    },
    progress() {
      return this.project.progress || 0;
    },
    // A BIA project's tasks are the system impact assessments, counted apart from the other tasks
    taskCount() {
      return (this.project.task_count || 0) + (this.project.system_risk_count || 0);
    },
    ringDash() {
      const circumference = 2 * Math.PI * 40;
      return (circumference * this.progress / 100) + ' ' + circumference;
    },
    isOverdue() {
      return this.project.status !== 'completed' && this.project.status !== 'closed' && this.days(this.project.end_date) < 0;
    }
  },
  methods: {
    days(value) {
      const moment = require('moment');
      return moment(String(value).substring(0, 10), 'YYYY-MM-DD').diff(moment().startOf('day'), 'days');
    },
    relative(value) {
      const days = this.days(value);
      if (days === 0) {
        return this.$t('projects.schedule.today');
      }
      return days > 0 ? this.$tc('projects.schedule.in_days', days, { count: days }) : this.$tc('projects.schedule.days_ago', -days, { count: -days });
    }
  }
}
</script>
